import React from 'react';
import { pdf } from '@react-pdf/renderer';

import FormResponsePdfDocument from '../components/form-response-pdf/form-response-pdf-document';
import { normalizeStoredFormSchema } from './form-io.util';
import { resolveFieldKindFromChecks, visitNestedColumns, visitNestedComponents } from './form-io-traverse.util';
import { EXCLUDED_SUBMISSION_DATA_KEYS } from './form-submission-constants.util';
import {
  formatCurrencyCell,
  formatFormSubmissionDatetimeCell,
  formatSelectboxCell,
  formatSubmissionDataCell,
  formatSurveyCell,
  extractSignatureOrFileUrl,
  isDataImageSignatureHref,
} from './form-submission-cell-format.util';
import { formatFormSubmissionCreatedAtForDisplay } from './form-submission-date.util';
import {
  collectSortedDataKeysFromSubmissions,
  collectSubmissionFieldMetaFromDefinition,
} from './form-submission-schema.util';
import { downloadBlob } from './csv-export.util';

const trimString = (value) => String(value ?? '').trim();

const shouldSkipFieldKey = (key) => {
  const k = key.trim();
  if (k === '') {
    return true;
  }
  return EXCLUDED_SUBMISSION_DATA_KEYS.has(k.toLowerCase());
};

const labelForComponentNode = (node, key) => {
  const raw = node?.label;
  if (typeof raw === 'string' && trimString(raw) !== '') {
    return trimString(raw);
  }
  return key;
};

const getValidatedFieldKey = (node) => {
  if (!node || typeof node !== 'object' || typeof node.key !== 'string') {
    return null;
  }
  const key = node.key.trim();
  return shouldSkipFieldKey(key) ? null : key;
};

const addLabelFromNode = (node, map) => {
  const key = getValidatedFieldKey(node);
  if (key === null || map.has(key)) {
    return;
  }
  map.set(key, labelForComponentNode(node, key));
};

const collectLabelsFromNode = (node, map) => {
  if (!node || typeof node !== 'object') {
    return;
  }
  addLabelFromNode(node, map);
  const visit = (child) => collectLabelsFromNode(child, map);
  visitNestedComponents(node, visit);
  visitNestedColumns(node, visit);
};

/**
 * Map Form.io field `key` → display `label` from the stored definition schema.
 * @param {unknown} definition — API form row with `schema`.
 * @returns {ReadonlyMap<string, string>}
 */
export const collectLabelsByKeyFromFormDefinition = (definition) => {
  const map = new Map();
  const form = normalizeStoredFormSchema(definition?.schema);
  visitNestedComponents(form, (child) => collectLabelsFromNode(child, map));
  return map;
};

const getSubmissionDataObject = (submission) => {
  const data = submission?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }
  return data;
};

const getUserDisplayName = (person) => {
  const username = trimString(person?.username);
  if (username !== '') {
    return username;
  }
  const name = `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim();
  if (name !== '') {
    return name;
  }
  return trimString(person?.docId);
};

/**
 * @param {unknown} rawValue
 * @returns {{ answer: string, signatureImageSrc: string | null }}
 */
const formatSignatureForPdf = (rawValue) => {
  const href = extractSignatureOrFileUrl(rawValue);
  if (!href) {
    return { answer: '', signatureImageSrc: null };
  }
  if (isDataImageSignatureHref(href)) {
    return { answer: '', signatureImageSrc: href };
  }
  return { answer: href, signatureImageSrc: null };
};

const PDF_FIELD_KIND_CHECKS = [
  ['signature', (key, ctx) => ctx.signatureKeys.has(key)],
  ['currency', (key, ctx) => ctx.currencyMeta.has(key)],
  ['selectbox', (key, ctx) => ctx.selectboxKeys.has(key)],
  ['survey', (key, ctx) => ctx.surveyKeys.has(key)],
  ['datetime', (key, ctx) => ctx.datetimeKeys.has(key)],
];

const resolvePdfFieldKind = (key, ctx) => resolveFieldKindFromChecks(PDF_FIELD_KIND_CHECKS, key, ctx);

const PDF_FIELD_FORMATTERS = {
  signature: (rawValue) => formatSignatureForPdf(rawValue),
  currency: (rawValue, key, ctx) => ({
    answer: formatCurrencyCell(rawValue, ctx.currencyMeta.get(key)),
    signatureImageSrc: null,
  }),
  selectbox: (rawValue) => ({ answer: formatSelectboxCell(rawValue), signatureImageSrc: null }),
  survey: (rawValue) => ({ answer: formatSurveyCell(rawValue), signatureImageSrc: null }),
  datetime: (rawValue) => ({
    answer: formatFormSubmissionDatetimeCell(rawValue),
    signatureImageSrc: null,
  }),
  default: (rawValue) => ({ answer: formatSubmissionDataCell(rawValue), signatureImageSrc: null }),
};

const formatAnswerForPdf = (rawValue, key, ctx) => {
  const kind = resolvePdfFieldKind(key, ctx);
  return PDF_FIELD_FORMATTERS[kind](rawValue, key, ctx);
};

/**
 * @param {{
 *   submission: object,
 *   formDefinition: unknown,
 *   signatureKeys: ReadonlySet<string>,
 *   currencyMeta: ReadonlyMap<string, string>,
 *   selectboxKeys: ReadonlySet<string>,
 *   surveyKeys: ReadonlySet<string>,
 *   datetimeKeys: ReadonlySet<string>,
 *   formTitle: string,
 * }} input
 */
export const buildFormResponsePdfPayload = ({
  submission,
  formDefinition,
  signatureKeys,
  currencyMeta,
  selectboxKeys,
  surveyKeys,
  datetimeKeys,
  formTitle,
}) => {
  const labelsByKey = collectLabelsByKeyFromFormDefinition(formDefinition);
  const dataKeys = collectSortedDataKeysFromSubmissions([submission]);
  const data = getSubmissionDataObject(submission);
  const ctx = { signatureKeys, currencyMeta, selectboxKeys, surveyKeys, datetimeKeys };
  const items = dataKeys.map((key) => {
    const question = labelsByKey.get(key) ?? key;
    const { answer, signatureImageSrc } = formatAnswerForPdf(data[key], key, ctx);
    return { answer, fieldKey: key, question, signatureImageSrc };
  });
  const docId = trimString(submission?.person?.docId);
  const header = {
    formTitle: trimString(formTitle) || 'Form response',
    location: trimString(submission?.location_name),
    submittedAt: formatFormSubmissionCreatedAtForDisplay(submission?.created_at),
    user: getUserDisplayName(submission?.person),
    ...(docId === '' ? {} : { docId }),
  };
  return { header, items };
};

const slugifySegment = (value) => {
  const s = trimString(value).replaceAll(/\s+/g, '-').replaceAll(/[^a-zA-Z0-9_-]/g, '');
  return s === '' ? 'response' : s.slice(0, 48);
};

const submissionCreatedAtToFilenamePart = (createdAt) => {
  if (createdAt == null || createdAt === '') {
    return 'unknown-time';
  }
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) {
    return 'unknown-time';
  }
  return d.toISOString().replaceAll(/[:.]/g, '-');
};

/**
 * @param {string} formTitle
 * @param {{ person?: { username?: string | null }, created_at?: string } | null | undefined} submission
 */
export const buildFormResponsePdfFilename = (formTitle, submission) => {
  const titlePart = slugifySegment(formTitle);
  const userPart = slugifySegment(submission?.person?.username ?? 'user');
  const created = submissionCreatedAtToFilenamePart(submission?.created_at);
  return `form-response-${titlePart}-${userPart}-${created}.pdf`;
};

/**
 * Builds and downloads a single-submission PDF.
 * @param {{
 *   submission: object,
 *   formDefinition: unknown,
 *   formTitle: string,
 *   signatureKeys: ReadonlySet<string>,
 *   currencyMeta: ReadonlyMap<string, string>,
 *   selectboxKeys: ReadonlySet<string>,
 *   surveyKeys: ReadonlySet<string>,
 *   datetimeKeys: ReadonlySet<string>,
 * }} input
 * @returns {Promise<void>}
 */
export const downloadFormResponsePdf = async (input) => {
  const payload = buildFormResponsePdfPayload(input);
  const filename = buildFormResponsePdfFilename(input.formTitle, input.submission);
  const blob = await pdf(<FormResponsePdfDocument {...payload} />).toBlob();
  downloadBlob(filename, blob);
};

/**
 * @param {{
 *   submission: object,
 *   formDefinition: unknown,
 *   formTitle: string,
 * }} input
 */
export const exportFormSubmissionAsPdf = async ({ submission, formDefinition, formTitle }) => {
  const fieldMeta = collectSubmissionFieldMetaFromDefinition(formDefinition);
  await downloadFormResponsePdf({
    submission,
    formDefinition,
    formTitle,
    ...fieldMeta,
  });
};
